/**
 * True pixel-art blit helpers.
 * Rules enforced here:
 * - sprites are authored on an integer grid
 * - draw with fillRect only (no arcs/gradients)
 * - scale is always an integer (2× / 3× / 4×)
 * - imageSmoothing stays off
 */

export type PixelChar =
  | "." // transparent
  | "O" // outline
  | "D" // dark shade
  | "M" // mid / body
  | "L" // light body
  | "H" // highlight
  | "A" // accent / markings
  | "E" // eye white (unused on chibi — eyes are solid)
  | "P" // eye / pupil (solid dark)
  | "K" // face skin
  | "N" // mouth
  | "U" // belly panel
  | "I" // belly badge
  | "S" // shiny spark
  | "R" // orb red
  | "r" // orb red shade
  | "C" // orb cream
  | "c" // orb cream shade
  | "B" // band / metal
  | "G" // glow
  | "T" // terrain sand
  | "t" // terrain sand shade
  | "W" // water
  | "w" // water highlight
  | "F" // foliage
  | "f"; // foliage shade

export type Palette = Partial<Record<Exclude<PixelChar, ".">, string>>;

export interface Sprite {
  w: number;
  h: number;
  /** Row-major string of PixelChars; length must be w * h. */
  pixels: string;
}

export function parseSprite(rows: string[]): Sprite {
  const h = rows.length;
  const w = rows[0]?.length ?? 0;
  if (!w || rows.some((r) => r.length !== w)) {
    throw new Error("sprite rows must be rectangular");
  }
  return { w, h, pixels: rows.join("") };
}

export function blitSprite(
  ctx: CanvasRenderingContext2D,
  sprite: Sprite,
  palette: Palette,
  x: number,
  y: number,
  scale: number,
  options?: { alpha?: number; flipX?: boolean },
): void {
  const s = Math.max(1, Math.round(scale));
  const alpha = options?.alpha ?? 1;
  const flipX = options?.flipX ?? false;
  const prevAlpha = ctx.globalAlpha;
  ctx.globalAlpha = alpha;
  ctx.imageSmoothingEnabled = false;

  for (let py = 0; py < sprite.h; py++) {
    for (let px = 0; px < sprite.w; px++) {
      const srcX = flipX ? sprite.w - 1 - px : px;
      const ch = sprite.pixels[py * sprite.w + srcX] as PixelChar;
      if (!ch || ch === ".") continue;
      const color = palette[ch as Exclude<PixelChar, ".">];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(Math.floor(x) + px * s, Math.floor(y) + py * s, s, s);
    }
  }
  ctx.globalAlpha = prevAlpha;
}

/** Snap a world position so sprites land on the pixel lattice. */
export function snap(n: number): number {
  return Math.round(n);
}
